import { ActionContext as VuexActionContext, Store } from 'vuex'
import { Namespace } from '@/store/Namespaces'

import { OrganisationProperty } from '@/types/landlord-order.types'

/* istanbul ignore file */

export const namespace = (fragment: string) => `${Namespace.LandlordOrder}/${Namespace.LandlordOrderOrganisation}/${fragment}`

enum Setter {
  SetCompanyCode = 'setCompanyCode',
  SetOrganisationCodeDirty = 'setOrganisationCodeDirty',
  SetCompanyName = 'setCompanyName',
  SetCompanyAuthorizedSignatory = 'setCompanyAuthorizedSignatory',
  SetKoANumber = 'setKoANumber',
  SetValidationIsOngoing = 'setValidationIsOngoing'
}

export enum Action {
  UpdateCompanyCode = 'updateCompanyCode',
  UpdateOrganisationCodeDirty = 'updateOrganisationCodeDirty',
  UpdateCompanyName = 'updateCompanyName',
  UpdateCompanyAuthorizedSignatory = 'updateCompanyAuthorizedSignatory',
  ResetOrganisationForm = 'resetOrganisationForm',
  UpdateKoANumber = 'updateKoANumber',
  SetValidationIsOngoing = 'setValidationIsOngoing'
}

export interface State {
  [OrganisationProperty.CompanyCode]: string;
  [OrganisationProperty.OrganisationCodeDirty]: boolean;
  [OrganisationProperty.CompanyName]: string;
  [OrganisationProperty.CompanyAuthorizedSignatory]: boolean;
  [OrganisationProperty.KoANumber]: string;
  [OrganisationProperty.ValidationIsOngoing]: boolean;
}

export type ActionContext = VuexActionContext<State, {}>

const landlordOrderOrganisationModule = {
  namespaced: true,
  state: {
    [OrganisationProperty.CompanyCode]: '',
    [OrganisationProperty.OrganisationCodeDirty]: true,
    [OrganisationProperty.CompanyName]: '',
    [OrganisationProperty.CompanyAuthorizedSignatory]: false,
    [OrganisationProperty.KoANumber]: '',
    [OrganisationProperty.ValidationIsOngoing]: false
  },
  getters: {
    [OrganisationProperty.CompanyCode]: (state: State) => state[OrganisationProperty.CompanyCode],
    [OrganisationProperty.OrganisationCodeDirty]: (state: State) => state[OrganisationProperty.OrganisationCodeDirty],
    [OrganisationProperty.CompanyName]: (state: State) => state[OrganisationProperty.CompanyName],
    [OrganisationProperty.CompanyAuthorizedSignatory]: (state: State) => state[OrganisationProperty.CompanyAuthorizedSignatory],
    [OrganisationProperty.KoANumber]: (state: State) => state[OrganisationProperty.KoANumber],
    [OrganisationProperty.ValidationIsOngoing]: (state: State) => state[OrganisationProperty.ValidationIsOngoing]
  },
  mutations: {
    [Setter.SetCompanyCode]: (state: State, value: string) => {
      state[OrganisationProperty.CompanyCode] = value
    },
    [Setter.SetOrganisationCodeDirty]: (state: State, value: boolean) => {
      state[OrganisationProperty.OrganisationCodeDirty] = value
    },
    [Setter.SetCompanyName]: (state: State, value: string) => {
      state[OrganisationProperty.CompanyName] = value
    },
    [Setter.SetCompanyAuthorizedSignatory]: (state: State, value: boolean) => {
      state[OrganisationProperty.CompanyAuthorizedSignatory] = value
    },
    [Setter.SetKoANumber]: (state: State, value: string) => {
      state[OrganisationProperty.KoANumber] = value
    },
    [Setter.SetValidationIsOngoing]: (state: State, value: boolean) => {
      state[OrganisationProperty.ValidationIsOngoing] = value
    }
  },
  actions: {
    [Action.UpdateCompanyCode]: ({ commit }: ActionContext, payload: string): void => {
      commit(Setter.SetCompanyCode, payload)
    },
    [Action.UpdateOrganisationCodeDirty]: ({ commit }: ActionContext, payload: boolean): void => {
      commit(Setter.SetOrganisationCodeDirty, payload)
    },
    [Action.UpdateCompanyName]: ({ commit }: ActionContext, payload: string): void => {
      commit(Setter.SetCompanyName, payload)
    },
    [Action.UpdateCompanyAuthorizedSignatory]: ({ commit }: ActionContext, payload: boolean): void => {
      commit(Setter.SetCompanyAuthorizedSignatory, payload)
    },
    [Action.UpdateKoANumber]: ({ commit }: ActionContext, payload: string): void => {
      commit(Setter.SetKoANumber, payload)
    },
    [Action.SetValidationIsOngoing]: ({ commit }: ActionContext, val: boolean): void => {
      commit(Setter.SetValidationIsOngoing, val)
    },
    [Action.ResetOrganisationForm]: ({ commit }: ActionContext): void => {
      commit(Setter.SetCompanyCode, '')
      commit(Setter.SetCompanyName, '')
      commit(Setter.SetCompanyAuthorizedSignatory, false)
      commit(Setter.SetKoANumber, '')
      commit(Setter.SetOrganisationCodeDirty, false)
    }
  }
}

export const getCompanyCode = (store: Store<{}>): string => store.getters[namespace(OrganisationProperty.CompanyCode)]
export const getOrganisationCodeDirty = (store: Store<{}>): boolean => store.getters[namespace(OrganisationProperty.OrganisationCodeDirty)]
export const getCompanyName = (store: Store<{}>): string => store.getters[namespace(OrganisationProperty.CompanyName)]
export const getCompanyAuthorizedSignatory = (store: Store<{}>): boolean => store.getters[namespace(OrganisationProperty.CompanyAuthorizedSignatory)]
export const getKoANumber = (store: Store<{}>): string => store.getters[namespace(OrganisationProperty.KoANumber)]
export const getValidationOngoing = (store: Store<{}>): string => store.getters[namespace(OrganisationProperty.ValidationIsOngoing)]

export const updateCompanyCode = (store: Store<{}>, payload: string): Promise<void> => store.dispatch(namespace(Action.UpdateCompanyCode), payload)
export const updateOrganisationCodeDirty = (store: Store<{}>, payload: boolean): Promise<void> => store.dispatch(namespace(Action.UpdateOrganisationCodeDirty), payload)
export const updateCompanyName = (store: Store<{}>, payload: string): Promise<void> => store.dispatch(namespace(Action.UpdateCompanyName), payload)
export const updateCompanyAuthorizedSignatory = (store: Store<{}>, payload: boolean): Promise<void> => store.dispatch(namespace(Action.UpdateCompanyAuthorizedSignatory), payload)
export const updateKoANumber = (store: Store<{}>, payload: string): Promise<void> => store.dispatch(namespace(Action.UpdateKoANumber), payload)
export const setValidationOngoing = (store: Store<{}>, payload: boolean): Promise<void> => store.dispatch(namespace(Action.SetValidationIsOngoing), payload)

export const resetOrganisationForm = (store: Store<{}>): Promise<void> => store.dispatch(namespace(Action.ResetOrganisationForm))

export default landlordOrderOrganisationModule
